import { join } from "lodash";
import { AugmentedMandate } from "../api/augmentedEntities/AugmentedMandate";
import { Buyer, EmbedAppliedFiscalDevice, Housing, Mandate, Program, Purchase, User } from "../api/Entities";
import { FurnituresVatRate, RentalGuaranteeVatRate } from "./Consts";
import { compareStrings } from "./Utils";

export interface Person {
  firstName: string;
  lastName: string;
}

export function sortPersonsByFirstName(p1: Person, p2: Person): number {
  const firstNameComparison = compareStrings(p1.firstName, p2.firstName);
  return firstNameComparison !== 0 ? firstNameComparison : compareStrings(p1.lastName, p2.lastName);
}

export function sortPersonsByLastName(p1: Person, p2: Person): number {
  const lastNameComparison = compareStrings(p1.lastName, p2.lastName);
  return lastNameComparison !== 0 ? lastNameComparison : compareStrings(p1.firstName, p2.firstName);
}

export function sortSellersByCompanyName(s1: User, s2: User): number {
  const companyComparison = compareStrings(s1.companyName ?? "", s2.companyName ?? "");
  return companyComparison !== 0 ? companyComparison : sortPersonsByFirstName(s1, s2);
}

export function sortMandatesPerHousingRef(m1: AugmentedMandate, m2: AugmentedMandate): number {
  const buildingComparison = compareStrings(m1.housing.building, m2.housing.building);
  return buildingComparison !== 0 ? buildingComparison : compareStrings(m1.housing.number, m2.housing.number);
}

export function getAttribution(attributable: { buyer: Buyer; coBuyer?: Buyer }): string {
  let txt = attributable.coBuyer ? "aux noms" : "au nom";
  txt += ` de ${attributable.buyer.firstName} ${attributable.buyer.lastName}`;
  if (attributable.coBuyer) txt += ` et ${attributable.coBuyer.firstName} ${attributable.coBuyer.lastName}`;
  return txt;
}

export function getFullName(p?: Person) {
  return p ? `${p.firstName} ${p.lastName}` : "";
}

export function getSellerCompanyAndName(s?: User) {
  if (!s) return "";
  const fullName = getFullName(s);
  return s.companyName ? `${s.companyName} (${fullName})` : fullName;
}

export function getHousingFullRef(h?: Housing) {
  return h ? `${h.building}${h.number}` : "";
}

export function formatFloor(f: number) {
  return f === 0 ? "RDC" : f === 1 ? "1er" : `${f}e`;
}

export type CommissionInfo = {
  commissionRate: number;
  commissionAmount: number;
};

export function getCommissionInfo(program: Program, afd: EmbedAppliedFiscalDevice, mandate: Mandate): CommissionInfo {
  let commissionAmount: number;
  let commissionRate = mandate.commissionRate;
  const atiComBasePrice = afd.realEstatePrice + afd.parkingPrice - mandate.cashback;
  const tfComBasePrice =
    afd.realEstatePrice / (1 + afd.vatRate) +
    afd.parkingPrice / (1 + afd.parkingVatRate) -
    mandate.cashback / afd.vatRate;
  switch (program.commissionMode) {
    case "ht_ht":
      commissionAmount = commissionRate * tfComBasePrice;
      break;
    case "ht_ttc":
      commissionAmount = commissionRate * atiComBasePrice;
      break;
    case "ttc_ttc":
      commissionRate *= 1.2;
      commissionAmount = commissionRate * atiComBasePrice;
      break;
  }
  return { commissionRate, commissionAmount };
}

export function shortenFiscalDeviceName(it: string | null): string {
  if (!it) return "";

  const split = it.trim().split(/\s+/);
  if (split.length > 1)
    return join(
      split.map((s) => /[A-Z]+/.exec(s) ?? s[0]),
      ""
    ).toLocaleUpperCase();

  return it.substring(0, 3);
}

export function getRentVatRateCoeff(program: Program): number {
  return program.isFurnished && !!program.leasetemplateId ? 1.1 : 1.2;
}

export function getTotalRent(afd: EmbedAppliedFiscalDevice): number {
  return (afd.housingRent ?? 0) + (afd.annexRent ?? 0);
}

export function getDefaultTotalRent(h: Housing): number {
  return getTotalRent(getDefaultAppliedFiscalDevice(h));
}

export function getYield(afd: EmbedAppliedFiscalDevice, program: Program, mandate?: Mandate): number {
  //yield is computed WITHOUT taking furnitures into account, nor cashback
  const atiPrice = afd.realEstatePrice + afd.parkingPrice + (mandate?.rentalGuaranteePrice ?? 0);
  const rent = getTotalRent(afd);
  if (program.showAtiYield) return (12 * rent) / atiPrice;
  const tfPrice =
    afd.realEstatePrice / (1 + afd.vatRate) +
    afd.parkingPrice / (1 + afd.parkingVatRate) +
    (mandate?.rentalGuaranteePrice ?? 0) / (1 + RentalGuaranteeVatRate);
  return (12 * rent) / getRentVatRateCoeff(program) / tfPrice;
}

export function getDefaultYield(h: Housing, program: Program, mandate?: Mandate): number {
  return getYield(getDefaultAppliedFiscalDevice(h), program, mandate);
}

export function getSellingPrice(afd: EmbedAppliedFiscalDevice, mandate?: Mandate, taxFree: boolean = false) {
  return taxFree
    ? afd.realEstatePrice / (1 + afd.vatRate) +
        afd.parkingPrice / (1 + afd.parkingVatRate) +
        (afd.furnituresPrice ?? 0) / (1 + FurnituresVatRate) +
        (mandate?.rentalGuaranteePrice ?? 0) / (1 + RentalGuaranteeVatRate)
    : afd.realEstatePrice + afd.parkingPrice + (afd.furnituresPrice ?? 0) + (mandate?.rentalGuaranteePrice ?? 0);
}

export function getDefaultSellingPrice(h: Housing, mandate?: Mandate, taxFree: boolean = false) {
  return getSellingPrice(getDefaultAppliedFiscalDevice(h), mandate, taxFree);
}

export function getDefaultAppliedFiscalDevice(h: Housing) {
  return h.devices.findOrThrow((afd) => afd.fiscaldeviceId === h.defaultFiscaldeviceId);
}

export function getBestAppliedFiscalDevice(h: Housing, purchase: Purchase | undefined) {
  const targetId = purchase ? purchase.fiscaldeviceId : h.defaultFiscaldeviceId;
  return h.devices.findOrThrow((afd) => afd.fiscaldeviceId === targetId);
}
